import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { API_ENDPOINTS } from '../../config/api.config';
import { STORAGE_KEYS } from '../../config/storage-keys.config';
import { unwrapApiData } from '../../utils/api-response.util';
import { readChannelPartnerContextFromToken } from '../../utils/jwt-payload.util';
import {
  mapAuthErrorCodeToLogoutReason,
  parseAuthErrorCode,
} from '../../utils/session-auth-error.util';
import { HttpService } from '../http/http.service';
import { StorageService } from '../storage/storage.service';
import { AuthService } from '../../../modules/auth/services/auth/auth.service';
import { OnboardingSessionService } from '../../../modules/onboarding/services/onboarding-session.service';
import { Router } from '@angular/router';
import { isPublicFrontendPath } from '../../utils/public-route.util';

interface RefreshTokenResponse {
  accessToken: string;
  refreshToken?: string;
}

type SessionKind = 'portal' | 'onboarding';

@Injectable({ providedIn: 'root' })
export class TokenRefreshService {
  private readonly http = inject(HttpService);
  private readonly authService = inject(AuthService);
  private readonly onboardingSession = inject(OnboardingSessionService);
  private readonly storageService = inject(StorageService);
  private readonly router = inject(Router);

  private isRefreshing = false;
  private refreshingKind: SessionKind | null = null;
  private readonly refreshTokenSubject = new BehaviorSubject<string | null>(
    null,
  );

  refreshTokens(failedRequestUrl?: string): Observable<string> {
    const kind = this.resolveSessionKind(failedRequestUrl);

    if (this.isRefreshing && this.refreshingKind === kind) {
      return this.refreshTokenSubject.pipe(
        filter((token): token is string => token !== null),
        take(1),
      );
    }

    this.isRefreshing = true;
    this.refreshingKind = kind;
    this.refreshTokenSubject.next(null);

    const refreshToken =
      kind === 'onboarding'
        ? this.onboardingSession.getRefreshToken()
        : this.storageService.getItem<string>(STORAGE_KEYS.REFRESH_TOKEN);

    if (!refreshToken) {
      this.isRefreshing = false;
      this.refreshingKind = null;
      if (!isPublicFrontendPath()) {
        this.handleRefreshFailure(kind);
      }
      return throwError(() => new Error('No refresh token available'));
    }

    return this.http
      .post<unknown>(API_ENDPOINTS.AUTH.REFRESH, { refreshToken })
      .pipe(
        switchMap(res => {
          const data = unwrapApiData<RefreshTokenResponse>(res);
          const newAccessToken = data?.accessToken?.trim();
          const newRefreshToken = data?.refreshToken?.trim();

          if (!newAccessToken) {
            return throwError(() => new Error('Invalid refresh response'));
          }

          if (kind === 'onboarding') {
            this.onboardingSession.updateTokens(
              newAccessToken,
              newRefreshToken,
            );
          } else {
            this.storageService.setItem(STORAGE_KEYS.AUTH_TOKEN, newAccessToken);
            if (newRefreshToken) {
              this.storageService.setItem(
                STORAGE_KEYS.REFRESH_TOKEN,
                newRefreshToken,
              );
            }
            this.syncChannelPartnerContextFromToken(newAccessToken);
          }

          this.refreshTokenSubject.next(newAccessToken);
          this.isRefreshing = false;
          this.refreshingKind = null;
          return [newAccessToken];
        }),
        catchError(err => {
          this.isRefreshing = false;
          this.refreshingKind = null;
          this.refreshTokenSubject.next(null);
          if (!isPublicFrontendPath()) {
            this.handleRefreshFailure(kind, err);
          }
          return throwError(() => err);
        }),
      );
  }

  private resolveSessionKind(failedRequestUrl?: string): SessionKind {
    const url = failedRequestUrl ?? '';
    if (
      url.includes('/v1/channel-partner/onboarding/') ||
      url.includes('/v1/channel-partners/verify-pan') ||
      url.includes('/v1/channel-partners/verify-gst') ||
      url.includes('/v1/integrations/surepass/') ||
      url.includes('/v1/integrations/idp/')
    ) {
      return 'onboarding';
    }
    // Surepass/IDP are used in both portal and registration; prefer onboarding
    // session when that is the only active auth context.
    if (this.onboardingSession.isActive() && !this.authService.isAuthenticated()) {
      return 'onboarding';
    }
    return 'portal';
  }

  private handleRefreshFailure(kind: SessionKind, err?: unknown): void {
    if (kind === 'onboarding') {
      this.onboardingSession.clear();
      void this.router.navigate(['/auth/login'], {
        queryParams: {
          message: encodeURIComponent(
            'Your onboarding session expired. Sign in or continue registration to resume.',
          ),
        },
      });
      return;
    }

    const code = parseAuthErrorCode(err);
    if (
      code === 'SESSION_TERMINATED_BY_NEW_LOGIN' ||
      code === 'SESSION_VERSION_MISMATCH'
    ) {
      this.authService.forceLogout({
        reason: 'SESSION_VERSION_MISMATCH',
        detail: 'Refresh failed: session terminated by new login',
      });
      return;
    }

    this.authService.forceLogout({
      reason: mapAuthErrorCodeToLogoutReason(code),
      detail: 'Refresh token failure',
    });
  }

  private syncChannelPartnerContextFromToken(accessToken: string): void {
    const context = readChannelPartnerContextFromToken(accessToken);
    if (context.portal !== 'CHANNEL_PARTNER_PORTAL') {
      return;
    }
    if (context.workspaceId) {
      this.storageService.setItem(
        STORAGE_KEYS.WORKSPACE_ID,
        context.workspaceId,
      );
    }
    if (context.channelPartnerId) {
      this.storageService.setItem(
        STORAGE_KEYS.CHANNEL_PARTNER_ID,
        context.channelPartnerId,
      );
    }
    const current =
      this.storageService.getItem<Record<string, unknown>>(
        STORAGE_KEYS.USER_DETAILS,
      ) ?? {};
    this.storageService.setItem(STORAGE_KEYS.USER_DETAILS, {
      ...current,
      ...(context.workspaceId ? { workspaceId: context.workspaceId } : {}),
      ...(context.channelPartnerId
        ? { channelPartnerId: context.channelPartnerId }
        : {}),
      ...(context.userType ? { userType: context.userType } : {}),
    });
  }
}
